// src/services/tokenPriceStore.js
// Centralized store for token prices with background updates - Enhanced with server API integration

// Imports moved to avoid circular dependency
import axios from 'axios';
import { ethers } from 'ethers';

// API URL (configurable for development vs production)
const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api'  // In production, use relative path to same server
  : 'http://localhost:3001/api'; // In development, use localhost

// ABI for Chainlink Price Feed (minimal interface)
const PRICE_FEED_ABI = [
  {
    "inputs": [],
    "name": "decimals",
    "outputs": [{ "internalType": "uint8", "name": "", "type": "uint8" }],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "description",
    "outputs": [{ "internalType": "string", "name": "", "type": "string" }],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "latestRoundData",
    "outputs": [
      { "internalType": "uint80", "name": "roundId", "type": "uint80" },
      { "internalType": "int256", "name": "answer", "type": "int256" },
      { "internalType": "uint256", "name": "startedAt", "type": "uint256" },
      { "internalType": "uint256", "name": "updatedAt", "type": "uint256" },
      { "internalType": "uint80", "name": "answeredInRound", "type": "uint80" }
    ],
    "stateMutability": "view",
    "type": "function"
  }
];

// Default Chainlink feed URLs for Sepolia testnet
const CHAINLINK_FEEDS = {
  ETH_USD: '0x694AA1769357215DE4FAC081bf1f309aDC325306',
  BTC_USD: '0x1b44F3514812d835EB1BDB0acB33d3fA3351Ee43',
  LINK_USD: '0xc59E3633BAAC79493d908e63626716e204A45EdF',
  DAI_USD: '0x14866185B1962B63C3Ea9E03Bc1da838bab34C19',
  USDC_USD: '0xA2F78ab2355fe2f984D808B5CeE7FD0A93D5270E',
  USDT_USD: '0xA2F78ab2355fe2f984D808B5CeE7FD0A93D5270E' // Using same as USDC for example
};

// Cache for prices and their timestamps
const priceCache = {
  lastUpdated: {},   // Timestamp of last update for each token
  prices: {}         // Cached price values
};

// Cache for Chainlink feed info
const chainlinkCache = {
  descriptions: {},  // Feed descriptions
  decimals: {}       // Feed decimals
};

// Track which tokens we've already warned about to avoid spamming the console
const warnedTokens = new Set();

// Get a Web3 provider for Sepolia testnet
function getProvider() {
  try {
    // First try to use the window's provider (MetaMask or similar)
    if (window.ethereum) {
      return new ethers.providers.Web3Provider(window.ethereum);
    }
    
    // Fallback to a public RPC URL
    return new ethers.providers.JsonRpcProvider('https://eth-sepolia.g.alchemy.com/v2/demo');
  } catch (error) {
    console.error('❌ Error creating provider:', error);
    return null;
  }
}

// Function to get the most up-to-date mappings for CoinGecko from API
async function getCoinGeckoMappings() {
  try {
    // Get mappings from the API
    const response = await fetch(`${API_URL}/mappings`);
    if (response.ok) {
      const mappings = await response.json();
      
      // Convert from { address: { id, name } } to { address: id }
      const result = {};
      Object.entries(mappings).forEach(([address, data]) => {
        result[address.toLowerCase()] = data.id;
      });
      
      console.log(`📊 Loaded ${Object.keys(result).length} CoinGecko mappings from API`);
      return result;
    }
  } catch (error) {
    console.error("❌ Error loading CoinGecko mappings from API:", error);
  }
  
  // Return empty object if failed
  return {};
}

// Function to get the most up-to-date Chainlink mappings (to be implemented later)
async function getChainlinkMappings() {
  // For now, return empty object as this wasn't part of your original implementation
  return {};
}

// Initialize mappings (will be updated later)
let coinGeckoMap = {};
let chainlinkMap = {};

// Get price from Chainlink Price Feed
async function getTokenPriceFromChainlink(tokenAddress, provider) {
  if (!tokenAddress || !provider) return null;
  
  const normalizedAddress = tokenAddress.toLowerCase();
  const currentTime = Date.now();
  const cacheTimeout = 3 * 60 * 1000; // 3 minutes in milliseconds
  
  // Check if we have a cached Chainlink price that's still valid
  if (
    priceCache.prices[`chainlink:${normalizedAddress}`] &&
    priceCache.lastUpdated[`chainlink:${normalizedAddress}`] &&
    currentTime - priceCache.lastUpdated[`chainlink:${normalizedAddress}`] < cacheTimeout
  ) {
    const cachedPrice = priceCache.prices[`chainlink:${normalizedAddress}`];
    console.log(`🔄 Using cached Chainlink price for ${normalizedAddress}: $${cachedPrice.toFixed(2)} (expires in ${Math.round((cacheTimeout - (currentTime - priceCache.lastUpdated[`chainlink:${normalizedAddress}`])) / 1000)}s)`);
    return cachedPrice;
  }

  try {
    // Get the price feed address for this token
    const priceFeedAddress = chainlinkMap[normalizedAddress];
    
    if (!priceFeedAddress) {
      if (!warnedTokens.has(`chainlink:${normalizedAddress}`)) {
        console.warn(`No Chainlink mapping for token ${normalizedAddress}.`);
        warnedTokens.add(`chainlink:${normalizedAddress}`);
      }
      return null;
    }
    
    console.log(`⛓️ Fetching price from Chainlink for ${normalizedAddress}...`);
    
    // Create contract interface to the price feed
    const priceFeed = new ethers.Contract(
      priceFeedAddress,
      PRICE_FEED_ABI,
      provider
    );
    
    // Get decimals and description if we don't have them cached
    if (!chainlinkCache.decimals[priceFeedAddress]) {
      try {
        const decimals = await priceFeed.decimals();
        chainlinkCache.decimals[priceFeedAddress] = decimals;
      } catch (err) {
        console.warn(`Failed to get decimals for price feed ${priceFeedAddress}. Using default of 8.`);
        chainlinkCache.decimals[priceFeedAddress] = 8;
      }
    }
    
    if (!chainlinkCache.descriptions[priceFeedAddress]) {
      try {
        const description = await priceFeed.description();
        chainlinkCache.descriptions[priceFeedAddress] = description;
      } catch (err) {
        console.warn(`Failed to get description for price feed ${priceFeedAddress}.`);
        chainlinkCache.descriptions[priceFeedAddress] = "Unknown Feed";
      }
    }
    
    // Get latest price data
    const [, price, , updatedAt] = await priceFeed.latestRoundData();
    
    // Make sure price is positive
    if (price <= 0) {
      console.error(`Invalid price (${price}) from Chainlink feed ${priceFeedAddress}`);
      return null;
    }
    
    // Convert price to a JavaScript number with proper decimal scaling
    const decimals = chainlinkCache.decimals[priceFeedAddress];
    const priceValue = Number(ethers.utils.formatUnits(price, decimals));
    
    // Check for stale data - if price data is older than 24 hours, consider it stale
    const updatedAtTimestamp = updatedAt.toNumber() * 1000; // Convert to milliseconds
    const now = Date.now();
    if (now - updatedAtTimestamp > 24 * 60 * 60 * 1000) {
      console.warn(`⚠️ Chainlink price for ${normalizedAddress} is stale (last updated ${new Date(updatedAtTimestamp).toLocaleString()})`);
    }
    
    console.log(`⛓️ Chainlink price for ${normalizedAddress}: $${priceValue.toFixed(2)} (${chainlinkCache.descriptions[priceFeedAddress]})`);
    
    // Cache the Chainlink price with timestamp
    priceCache.prices[`chainlink:${normalizedAddress}`] = priceValue;
    priceCache.lastUpdated[`chainlink:${normalizedAddress}`] = currentTime;
    
    return priceValue;
  } catch (error) {
    console.error(`❌ Error fetching Chainlink price for ${normalizedAddress}:`, error.message);
    return null;
  }
}

// Get prices from CoinGecko API
async function getTokenPriceFromCoinGecko(tokenAddress) {
  if (!tokenAddress) return 0;
  
  const normalizedAddress = tokenAddress.toLowerCase();
  const currentTime = Date.now();
  const cacheTimeout = 3 * 60 * 1000; // 3 minutes in milliseconds
  
  // Check if we have a cached CoinGecko price that's still valid
  if (
    priceCache.prices[`coingecko:${normalizedAddress}`] &&
    priceCache.lastUpdated[`coingecko:${normalizedAddress}`] &&
    currentTime - priceCache.lastUpdated[`coingecko:${normalizedAddress}`] < cacheTimeout
  ) {
    const cachedPrice = priceCache.prices[`coingecko:${normalizedAddress}`];
    console.log(`🔄 Using cached CoinGecko price for ${normalizedAddress}: $${cachedPrice.toFixed(2)} (expires in ${Math.round((cacheTimeout - (currentTime - priceCache.lastUpdated[`coingecko:${normalizedAddress}`])) / 1000)}s)`);
    return cachedPrice;
  }

  try {
    // Make sure we have the latest mappings
    if (Object.keys(coinGeckoMap).length === 0) {
      coinGeckoMap = await getCoinGeckoMappings();
    }
    
    // Use the fetched mappings
    const coinId = coinGeckoMap[normalizedAddress];
    
    if (!coinId) {
      if (!warnedTokens.has(`coingecko:${normalizedAddress}`)) {
        console.warn(`No CoinGecko mapping for token ${normalizedAddress}.`);
        warnedTokens.add(`coingecko:${normalizedAddress}`);
      }
      return 0;
    }
    
    console.log(`📊 Fetching price from CoinGecko for ${coinId}...`);
    
    // CoinGecko API is free and doesn't require API keys for basic usage
    const response = await axios.get(
      `https://api.coingecko.com/api/v3/simple/price`, 
      {
        params: {
          ids: coinId,
          vs_currencies: 'usd'
        }
      }
    );
    
    // Extract the price from the response
    if (response.data && response.data[coinId] && response.data[coinId].usd) {
      const price = response.data[coinId].usd;
      console.log(`✅ CoinGecko price for ${coinId}: $${price.toFixed(2)}`);
      
      // Cache the CoinGecko price with timestamp
      priceCache.prices[`coingecko:${normalizedAddress}`] = price;
      priceCache.lastUpdated[`coingecko:${normalizedAddress}`] = currentTime;
      
      return price;
    } else {
      console.error("❌ Unexpected CoinGecko API response format");
      return 0;
    }
  } catch (error) {
    console.error(`❌ Error fetching CoinGecko price:`, error.message);
    return 0;
  }
}

// Get a token price with priority order: Chainlink -> CoinGecko -> Local fallback
async function getTokenPrice(tokenAddress) {
  if (!tokenAddress) return 0;
  
  const normalizedAddress = tokenAddress.toLowerCase();
  const provider = getProvider();
  
  // Try to get price from Chainlink first (most reliable)
  if (provider) {
    const chainlinkPrice = await getTokenPriceFromChainlink(normalizedAddress, provider);
    if (chainlinkPrice !== null && chainlinkPrice > 0) {
      return chainlinkPrice;
    }
  }
  
  // If no Chainlink price, try CoinGecko
  const coingeckoPrice = await getTokenPriceFromCoinGecko(normalizedAddress);
  if (coingeckoPrice > 0) {
    return coingeckoPrice;
  }
  
  // If no prices found, return 0
  return 0;
}

// Refresh mappings
async function refreshMappings() {
  coinGeckoMap = await getCoinGeckoMappings();
  chainlinkMap = await getChainlinkMappings();
  console.log('🔄 Price feed mappings refreshed');
  return { coinGeckoMap, chainlinkMap };
}

// Singleton store for prices across the application
class TokenPriceStore {
  constructor() {
    // Core data storage
    this.prices = {};
    this.lastUpdated = {};
    this.isUpdating = false;
    this.updateInterval = 3 * 60 * 1000; // 3 minutes
    this.updateTimer = null;
    this.listeners = [];
    this.initialized = false;
    
    // Data source tracking
    this.dataSources = {};  // Keep track of which source we got the price from
    
    // Track which tokens we're monitoring
    this.monitoredTokens = new Set();
    
    // Statistics for monitoring
    this.stats = {
      totalUpdates: 0,
      successfulUpdates: 0,
      failedUpdates: 0,
      lastUpdateTime: null,
      averageUpdateTime: 0,
      chainlinkPrices: 0,
      coingeckoPrices: 0
    };
  }
  
  // Initialize the store and start background updates
  async initialize() {
    if (this.initialized) return this;
    
    console.log('🚀 Initializing TokenPriceStore');
    
    // Load monitored tokens from API
    await this.loadMonitoredTokensFromStorage();
    
    // Load price data from API
    await this.loadFromStorage();
    
    // Load mappings
    await refreshMappings();
    
    // Start the background update cycle
    this.startBackgroundUpdates();
    
    // Mark as initialized
    this.initialized = true;
    
    return this;
  }
  
  // Load prices from API server
  async loadFromStorage() {
    try {
      const response = await fetch(`${API_URL}/prices`);
      if (!response.ok) {
        console.error('Failed to load prices from API:', response.statusText);
        return;
      }
      
      const data = await response.json();
      
      if (data.prices) {
        this.prices = data.prices;
        console.log(`📊 Loaded ${Object.keys(this.prices).length} cached prices from API server`);
        
        // Also log the actual prices for debugging
        const priceEntries = Object.entries(this.prices).slice(0, 5); // Show first 5 prices
        if (priceEntries.length > 0) {
          console.log('Sample cached prices:');
          priceEntries.forEach(([address, price]) => {
            const shortAddress = address.includes(':') 
              ? `${address.split(':')[0]}:${address.split(':')[1].substring(0, 6)}...` 
              : `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
            console.log(`- ${shortAddress}: $${price.toFixed(2)}`);
          });
        }
      }
      
      if (data.lastUpdated) {
        this.lastUpdated = data.lastUpdated;
      }
      
      if (data.stats) {
        this.stats = data.stats;
      }
      
      if (data.dataSources) {
        this.dataSources = data.dataSources;
      }
      
      // Notify any listeners about the loaded data
      if (Object.keys(this.prices).length > 0) {
        this.notifyListeners();
      }
    } catch (error) {
      console.error('❌ Error loading prices from API server:', error);
    }
  }
  
  // Save prices to API server
  async saveToStorage() {
    try {
      const data = {
        prices: this.prices,
        lastUpdated: this.lastUpdated,
        stats: this.stats,
        dataSources: this.dataSources
      };
      
      const response = await fetch(`${API_URL}/prices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save prices: ${response.statusText}`);
      }
      
      console.log('✅ Saved prices to API server');
    } catch (error) {
      console.error('❌ Error saving prices to API server:', error);
    }
  }
  
  // Load monitored tokens from API server
  async loadMonitoredTokensFromStorage() {
    try {
      const response = await fetch(`${API_URL}/monitored-tokens`);
      
      if (!response.ok) {
        console.error('Failed to load monitored tokens from API:', response.statusText);
        return;
      }
      
      const tokenArray = await response.json();
      this.monitoredTokens = new Set(tokenArray);
      console.log(`🔍 Loaded ${this.monitoredTokens.size} monitored tokens from API server`);
    } catch (error) {
      console.error('❌ Error loading monitored tokens from API server:', error);
      this.monitoredTokens = new Set();
    }
  }
  
  // Save monitored tokens to API server
  async saveMonitoredTokensToStorage() {
    try {
      const tokenArray = Array.from(this.monitoredTokens);
      
      const response = await fetch(`${API_URL}/monitored-tokens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tokenArray)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save monitored tokens: ${response.statusText}`);
      }
      
      console.log('✅ Saved monitored tokens to API server');
    } catch (error) {
      console.error('❌ Error saving monitored tokens to API server:', error);
    }
  }
  
  // Start the background update cycle
  startBackgroundUpdates() {
    if (this.updateTimer) {
      clearInterval(this.updateTimer);
    }
    
    // Initial update
    this.updateAllPrices();
    
    // Set up recurring updates
    this.updateTimer = setInterval(() => {
      this.updateAllPrices();
    }, this.updateInterval);
    
    console.log(`🔄 Background price updates started (every ${this.updateInterval / 60000} minutes)`);
  }
  
  // Stop background updates
  stopBackgroundUpdates() {
    if (this.updateTimer) {
      clearInterval(this.updateTimer);
      this.updateTimer = null;
      console.log('⏹️Background price updates stopped');
    }
  }
  
  // Add tokens to monitor
  async addTokensToMonitor(tokenAddresses) {
    if (!Array.isArray(tokenAddresses)) {
      tokenAddresses = [tokenAddresses];
    }
    
    let newTokensAdded = false;
    
    tokenAddresses.forEach(address => {
      if (address) {
        const normalizedAddress = address.toLowerCase();
        
        if (!this.monitoredTokens.has(normalizedAddress)) {
          this.monitoredTokens.add(normalizedAddress);
          newTokensAdded = true;
          console.log(`➕ Added ${normalizedAddress} to monitored tokens`);
        }
      }
    });
    
    if (newTokensAdded) {
      // Save updated list to API
      await this.saveMonitoredTokensToStorage();
      
      // Trigger an immediate update for new tokens
      this.updateAllPrices();
    }
  }
  
  // Remove tokens from monitoring
  async removeTokensFromMonitor(tokenAddresses) {
    if (!Array.isArray(tokenAddresses)) {
      tokenAddresses = [tokenAddresses];
    }
    
    let tokensRemoved = false;
    
    tokenAddresses.forEach(address => {
      if (address) {
        const normalizedAddress = address.toLowerCase();
        
        if (this.monitoredTokens.has(normalizedAddress)) {
          this.monitoredTokens.delete(normalizedAddress);
          tokensRemoved = true;
          console.log(`➖ Removed ${normalizedAddress} from monitored tokens`);
        }
      }
    });
    
    if (tokensRemoved) {
      // Save updated list to API
      await this.saveMonitoredTokensToStorage();
    }
  }
  
  // Update prices for all monitored tokens
  async updateAllPrices() {
    if (this.isUpdating || this.monitoredTokens.size === 0) {
      return;
    }
    
    // Refresh mappings before updating prices
    await refreshMappings();
    
    this.isUpdating = true;
    const startTime = Date.now();
    console.log(`🔄 Updating prices for ${this.monitoredTokens.size} tokens at ${new Date().toLocaleTimeString()}`);
    
    try {
      const tokenArray = Array.from(this.monitoredTokens);
      let updatedCount = 0;
      let chainlinkCount = 0;
      let coingeckoCount = 0;
      
      // Track which tokens actually need updates (check if they're stale)
      const tokensToUpdate = tokenArray.filter(address => {
        const currentTime = Date.now();
        const lastUpdateTime = this.lastUpdated[address] || 0;
        const timeSinceLastUpdate = currentTime - lastUpdateTime;
        
        // Only include token if it needs an update (cache expired or no data)
        return timeSinceLastUpdate >= this.updateInterval || !this.prices[address];
      });
      
      // Don't proceed if we don't have any tokens to update
      if (tokensToUpdate.length === 0) {
        console.log('✅ All token prices are fresh, skipping update cycle');
        this.isUpdating = false;
        return;
      }
      
      console.log(`🔹 Found ${tokensToUpdate.length} tokens that need price updates`);
      
      // Process tokens sequentially to avoid rate limiting
      for (const address of tokensToUpdate) {
        try {
          // Use our priority function to get the price from best available source
          const price = await getTokenPrice(address);
          
          if (price !== undefined && price !== null && price > 0) {
            this.prices[address] = price;
            this.lastUpdated[address] = Date.now();
            
            // Track which data source was used
            if (chainlinkMap[address]) {
              this.dataSources[address] = 'chainlink';
              chainlinkCount++;
            } else if (coinGeckoMap[address]) {
              this.dataSources[address] = 'coingecko';
              coingeckoCount++;
            } else {
              this.dataSources[address] = 'unknown';
            }
            
            updatedCount++;
          }
          
          // Small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (error) {
          console.error(`❌ Error updating price for ${address}:`, error);
          this.stats.failedUpdates++;
        }
      }
      
      this.stats.totalUpdates++;
      this.stats.successfulUpdates += updatedCount;
      this.stats.lastUpdateTime = new Date();
      this.stats.chainlinkPrices += chainlinkCount;
      this.stats.coingeckoPrices += coingeckoCount;
      
      const updateDuration = Date.now() - startTime;
      this.stats.averageUpdateTime = (this.stats.averageUpdateTime * (this.stats.totalUpdates - 1) + updateDuration) / this.stats.totalUpdates;
      
      console.log(`✅ Updated ${updatedCount} token prices (${chainlinkCount} Chainlink, ${coingeckoCount} CoinGecko) in ${updateDuration}ms`);
      
      // Save to API server
      await this.saveToStorage();
      
      // Notify listeners
      this.notifyListeners();
    } catch (error) {
      console.error('❌ Error in price update cycle:', error);
    } finally {
      this.isUpdating = false;
    }
  }
  
  // Force an immediate update for specific tokens
  async forceUpdate(tokenAddresses) {
    if (!Array.isArray(tokenAddresses)) {
      tokenAddresses = [tokenAddresses];
    }
    
    if (this.isUpdating) {
      console.log('⏳ Update already in progress, waiting...');
      await new Promise(resolve => {
        const checkInterval = setInterval(() => {
          if (!this.isUpdating) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
      });
    }
    
    // Refresh mappings before force update
    await refreshMappings();
    
    this.isUpdating = true;
    console.log(`🔄 Force updating prices for ${tokenAddresses.length} tokens`);
    
    try {
      let chainlinkCount = 0;
      let coingeckoCount = 0;
      
      for (const address of tokenAddresses) {
        if (address) {
          const normalizedAddress = address.toLowerCase();
          try {
            // Get price using our priority getter
            const price = await getTokenPrice(normalizedAddress);
            
            if (price !== undefined && price !== null && price > 0) {
              this.prices[normalizedAddress] = price;
              this.lastUpdated[normalizedAddress] = Date.now();
              
              // Track data source
              if (chainlinkMap[normalizedAddress]) {
                this.dataSources[normalizedAddress] = 'chainlink';
                chainlinkCount++;
              } else if (coinGeckoMap[normalizedAddress]) {
                this.dataSources[normalizedAddress] = 'coingecko';
                coingeckoCount++;
              } else {
                this.dataSources[normalizedAddress] = 'unknown';
              }
              
              // Add to monitored tokens if not already there
              if (!this.monitoredTokens.has(normalizedAddress)) {
                this.monitoredTokens.add(normalizedAddress);
                await this.saveMonitoredTokensToStorage();
              }
            }
            
            // Small delay to avoid rate limiting
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (error) {
            console.error(`❌ Error force updating price for ${normalizedAddress}:`, error);
          }
        }
      }
      
      // Update stats
      this.stats.chainlinkPrices += chainlinkCount;
      this.stats.coingeckoPrices += coingeckoCount;
      
      // Save to API server
      await this.saveToStorage();
      
      // Notify listeners
      this.notifyListeners();
      
      console.log(`✅ Force update completed (${chainlinkCount} Chainlink, ${coingeckoCount} CoinGecko)`);
    } catch (error) {
      console.error('❌ Error in force update:', error);
    } finally {
      this.isUpdating = false;
    }
  }
  
  // Reset to default tokens and mappings from server
  async resetToDefaults() {
    try {
      const response = await fetch(`${API_URL}/reset-to-defaults`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to reset to defaults: ${response.statusText}`);
      }
      
      console.log('✅ Reset to default tokens and mappings');
      
      // Reload data from server
      await this.loadMonitoredTokensFromStorage();
      await refreshMappings();
      
      // Trigger update for all tokens
      this.updateAllPrices();
      
      return true;
    } catch (error) {
      console.error('❌ Error resetting to defaults:', error);
      return false;
    }
  }
  
  // Get the data source for a token price
  getDataSource(tokenAddress) {
    if (!tokenAddress) return 'unknown';
    
    const normalizedAddress = tokenAddress.toLowerCase();
    return this.dataSources[normalizedAddress] || 'unknown';
  }
  
  // Get the current price for a token
  getPrice(tokenAddress) {
    if (!tokenAddress) return 0;
    
    const normalizedAddress = tokenAddress.toLowerCase();
    
    // Add to monitored tokens if not already monitoring
    if (!this.monitoredTokens.has(normalizedAddress)) {
      this.addTokensToMonitor(normalizedAddress);
    }
    
    return this.prices[normalizedAddress] || 0;
  }
  
  // Get prices for multiple tokens
  getPrices(tokenAddresses = []) {
    if (!tokenAddresses || tokenAddresses.length === 0) return {};
    
    const result = {};
    
    // Add all tokens to monitoring if not already
    this.addTokensToMonitor(tokenAddresses);
    
    tokenAddresses.forEach(address => {
      if (address) {
        const normalizedAddress = address.toLowerCase();
        result[normalizedAddress] = this.prices[normalizedAddress] || 0;
      }
    });
    
    return result;
  }
  
  // Get the last update time for a token
  getLastUpdated(tokenAddress) {
    if (!tokenAddress) return null;
    
    const normalizedAddress = tokenAddress.toLowerCase();
    return this.lastUpdated[normalizedAddress] ? new Date(this.lastUpdated[normalizedAddress]) : null;
  }
  
  // Check if a price is stale (older than the update interval)
  isPriceStale(tokenAddress) {
    if (!tokenAddress) return true;
    
    const normalizedAddress = tokenAddress.toLowerCase();
    const lastUpdate = this.lastUpdated[normalizedAddress];
    
    if (!lastUpdate) return true;
    
    const currentTime = Date.now();
    const timeSinceLastUpdate = currentTime - lastUpdate;
    
    return timeSinceLastUpdate > this.updateInterval;
  }
  
  // Add a listener for price updates
  addListener(callback) {
    if (typeof callback === 'function' && !this.listeners.includes(callback)) {
      this.listeners.push(callback);
      return true;
    }
    return false;
  }
  
  // Remove a listener
  removeListener(callback) {
    const index = this.listeners.indexOf(callback);
    if (index !== -1) {
      this.listeners.splice(index, 1);
      return true;
    }
    return false;
  }
  
  // Notify all listeners of price updates
  notifyListeners() {
    if (this.listeners.length > 0) {
      const data = {
        prices: { ...this.prices },
        lastUpdated: { ...this.lastUpdated },
        stats: { ...this.stats },
        dataSources: { ...this.dataSources }
      };
      
      this.listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('❌ Error in price update listener:', error);
        }
      });
    }
  }
  
  // Get stats about the price store
  getStats() {
    return {
      ...this.stats,
      monitoredTokenCount: this.monitoredTokens.size,
      cachedPriceCount: Object.keys(this.prices).length,
      chainlinkCount: Object.values(this.dataSources).filter(source => source === 'chainlink').length,
      coingeckoCount: Object.values(this.dataSources).filter(source => source === 'coingecko').length
    };
  }
}

// Create and export the singleton instance
const tokenPriceStore = new TokenPriceStore();
export default tokenPriceStore;